import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { 
  MapPinIcon, 
  PlusIcon, 
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface LocationSelectorProps {
  onLocationSelected?: () => void;
}

export function LocationSelector({ onLocationSelected }: LocationSelectorProps) {
  const { selectLocation, createLocation, isLoading, availableLocations, user } = useAuth();
  const { t } = useTranslation();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Form state for creating new location
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
  });

  useEffect(() => {
    console.log('LocationSelector: availableLocations from AuthContext:', availableLocations);
    console.log('LocationSelector: user role:', user?.role);
    
    // Use availableLocations from AuthContext (already fetched after login)
    if (availableLocations && availableLocations.length > 0) {
      console.log('LocationSelector: Using availableLocations from AuthContext:', availableLocations);
      setLoading(false);
      return;
    }
    
    // Fallback: try localStorage if availableLocations is empty
    try {
      const storedLocations = localStorage.getItem('available_locations');
      if (storedLocations) {
        const locationsData = JSON.parse(storedLocations);
        console.log('LocationSelector: Loaded locations from localStorage:', locationsData);
      }
    } catch (err: any) {
      console.error('Error loading locations from localStorage:', err);
    }
    
    console.log('LocationSelector: Waiting for locations to be loaded by AuthContext');
    setLoading(false);
  }, [availableLocations, user]);

  const handleSelectLocation = async (locationId: string) => {
    try {
      console.log('Selecting location with ID:', locationId);
      if (!locationId) {
        throw new Error('Location ID is required');
      }
      await selectLocation(locationId);
      // Mark that user is coming from login flow
      sessionStorage.setItem('fromLogin', 'true');
      onLocationSelected?.();
    } catch (err: any) {
      console.error('Error selecting location:', err);
      setError(err.message || t('failedToSelectLocation'));
    }
  };

  const handleCreateLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      await createLocation(newLocation);
      setShowCreateForm(false);
      setNewLocation({ name: '', address: '', phone: '', email: '' });
      // Mark that user is coming from login flow
      sessionStorage.setItem('fromLogin', 'true');
      onLocationSelected?.();
    } catch (err: any) {
      setError(err.message || t('failedToCreateLocation'));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewLocation(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <MapPinIcon className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('selectYourLocation')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('chooseLocationWhereYoullBeWorkingToday')}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        {!showCreateForm ? (
          <div className="space-y-4">
            {availableLocations && availableLocations.length > 0 ? (
              <div className="space-y-3">
                {availableLocations.map((location) => (
                  <button
                    key={location.id}
                    onClick={() => handleSelectLocation(location.id)}
                    disabled={isLoading}
                    className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors disabled:opacity-50"
                  >
                    <div className="flex items-center space-x-3">
                      <MapPinIcon className="h-5 w-5 text-gray-400" />
                      <div className="text-left">
                        <div className="font-medium text-gray-900">{location.name}</div>
                        {location.address && (
                          <div className="text-sm text-gray-500">{location.address}</div>
                        )}
                      </div>
                    </div>
                    <CheckIcon className="h-5 w-5 text-primary-600" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MapPinIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('noLocations')}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {user?.role === 'admin' 
                    ? t('createYourFirstLocationToGetStarted')
                    : t('pleaseContactAdministratorForLocationAccess')}
                </p>
              </div>
            )}

            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full flex items-center justify-center space-x-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors"
            >
              <PlusIcon className="h-5 w-5 text-gray-400" />
              <span className="text-gray-600">{t('createNewLocation')}</span>
            </button>
          </div>
        ) : (
          <form onSubmit={handleCreateLocation} className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">{t('createNewLocation')}</h3>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                {t('locationName')} *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={newLocation.name}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('locationNamePlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                {t('address')}
              </label>
              <input
                id="address"
                name="address"
                type="text"
                value={newLocation.address}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('addressPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                {t('phone')}
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={newLocation.phone}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('phonePlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={newLocation.email}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('locationEmailPlaceholder')}
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={isLoading || !newLocation.name.trim()}
                className="flex-1 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isLoading ? t('creating') : t('createLocation')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
